import React, { useRef } from "react";
import { api, fileUrl } from "../lib/api";
import { ChevronUp, ChevronDown, Trash2, GripVertical } from "lucide-react";
import { BlockView } from "./BlockRenderer";
import RichTextEditor from "./RichTextEditor";

export const BLOCK_TYPES = [
  { type: "heading", label: "Titre" },
  { type: "text", label: "Texte" },
  { type: "image", label: "Image" },
  { type: "video", label: "Vidéo (fichier)" },
  { type: "audio", label: "Audio" },
  { type: "youtube", label: "YouTube" },
  { type: "vimeo", label: "Vimeo" },
  { type: "file", label: "Fichier / PDF" },
  { type: "callout", label: "Encadré" },
  { type: "quote", label: "Citation" },
  { type: "divider", label: "Séparateur" },
];

export default function BlockEditor({ block, onChange, onRemove, onMove, isFirst, isLast }) {
  const c = block.content || {};
  const update = (key, value) => onChange({ ...block, content: { ...c, [key]: value } });
  const fileInput = useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    if (block.type === "file" || block.type === "audio") {
      onChange({ ...block, content: { ...c, url: data.url, name: data.filename } });
    } else {
      onChange({ ...block, content: { ...c, url: data.url } });
    }
  };

  return (
    <div className="card p-4 mb-3 group" data-testid={`block-edit-${block.id}`}>
      <div className="flex items-center justify-between mb-2 text-sm">
        <span className="font-semibold flex items-center gap-2"><GripVertical className="w-4 h-4 text-gray-400" /> {BLOCK_TYPES.find((b) => b.type === block.type)?.label || block.type}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={isFirst} className="p-1 disabled:opacity-30" title="Monter"><ChevronUp className="w-4 h-4" /></button>
          <button onClick={() => onMove(1)} disabled={isLast} className="p-1 disabled:opacity-30" title="Descendre"><ChevronDown className="w-4 h-4" /></button>
          <button onClick={onRemove} className="p-1 text-red-500" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {block.type === "heading" && (
        <div className="space-y-2">
          <input className="input" placeholder="Texte du titre" value={c.text || ""} onChange={(e) => update("text", e.target.value)} />
          <select className="input" value={c.level || 2} onChange={(e) => update("level", parseInt(e.target.value))}>
            <option value={1}>H1 (très grand)</option>
            <option value={2}>H2 (grand)</option>
            <option value={3}>H3 (moyen)</option>
            <option value={4}>H4 (petit)</option>
            <option value={5}>H5</option>
            <option value={6}>H6</option>
          </select>
        </div>
      )}
      {block.type === "text" && (
        <RichTextEditor
          value={c.text || ""}
          onChange={(value) => update("text", value)}
          placeholder="Texte..."
          minHeight={180}
        />
      )}
      {(block.type === "image" || block.type === "video" || block.type === "audio") && (
        <div className="space-y-2">
          <input
            className="input"
            placeholder={`URL de ${block.type === "image" ? "l'image" : block.type === "video" ? "la vidéo" : "l'audio"} ou upload`}
            value={c.url || ""}
            onChange={(e) => update("url", e.target.value)}
          />
          <input
            ref={fileInput}
            type="file"
            onChange={handleFile}
            className="hidden"
            accept={block.type === "image" ? "image/*" : block.type === "video" ? "video/*" : "audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/flac,audio/x-flac,audio/wav,audio/x-wav,audio/ogg,audio/aac,.mp3,.m4a,.flac,.wav,.ogg,.oga,.opus,.aac"}
          />
          <button onClick={() => fileInput.current.click()} className="btn-ghost text-sm">
            📎 Uploader un fichier
          </button>
          {block.type === "audio" && c.name && <p className="text-xs text-gray-500">Fichier audio : {c.name}</p>}
          <input className="input" placeholder="Légende (facultatif)" value={c.caption || ""} onChange={(e) => update("caption", e.target.value)} />
        </div>
      )}
      {(block.type === "youtube" || block.type === "vimeo") && (
        <div className="space-y-2">
          <input className="input" placeholder={`URL ${block.type === "youtube" ? "YouTube" : "Vimeo"}`} value={c.url || ""} onChange={(e) => update("url", e.target.value)} />
          <input className="input" placeholder="Légende (facultatif)" value={c.caption || ""} onChange={(e) => update("caption", e.target.value)} />
        </div>
      )}
      {block.type === "file" && (
        <div className="space-y-2">
          <input ref={fileInput} type="file" onChange={handleFile} className="hidden" />
          <button onClick={() => fileInput.current.click()} className="btn-ghost">📎 Choisir un fichier</button>
          {c.url && <p className="text-xs text-gray-500">{c.name} — <a href={fileUrl(c.url)} target="_blank" rel="noopener noreferrer" className="text-brand-green underline">aperçu</a></p>}
        </div>
      )}
      {block.type === "quote" && (
        <div className="space-y-2">
          <RichTextEditor value={c.text || ""} onChange={(value) => update("text", value)} placeholder="Citation" minHeight={120} />
          <input className="input" placeholder="Auteur (facultatif)" value={c.author || ""} onChange={(e) => update("author", e.target.value)} />
        </div>
      )}
      {block.type === "callout" && (
        <div className="space-y-2">
          <input className="input" placeholder="Titre (facultatif)" value={c.title || ""} onChange={(e) => update("title", e.target.value)} />
          <RichTextEditor value={c.text || ""} onChange={(value) => update("text", value)} placeholder="Texte" minHeight={120} />
          <select className="input" value={c.variant || "tip"} onChange={(e) => update("variant", e.target.value)}>
            <option value="tip">Astuce (vert)</option>
            <option value="warning">Important (orange)</option>
          </select>
        </div>
      )}
      {block.type === "divider" && <p className="text-sm text-gray-500">Une ligne de séparation sera affichée.</p>}

      <div className="mt-3 pt-3 border-t border-emerald-100">
        <p className="label mb-2">Aperçu</p>
        <BlockView block={block} />
      </div>
    </div>
  );
}
